import React from 'react';
import { motion } from 'framer-motion';
import { GitBranch, Cpu, Lock, Network, FileText, BookOpen } from 'lucide-react';
import { Link } from 'react-router-dom';

const Home = () => {
  
  return (
    <div className="min-h-screen pt-24 pb-16 bg-black text-white">
      <div className="container mx-auto px-4">
        {/* Hero Section */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center py-8 mb-8"
        >
          <h1 className="text-7xl md:text-9xl font-black mb-6 tracking-tight text-white">
            AVO Protocol
          </h1>
          
          {/* FlowConsensus Hero Highlight */}
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="mb-4"
          >
            <div className="flex items-center justify-center gap-4 mb-2">
              <Network className="w-10 h-10 text-white" />
              <h2 className="text-3xl md:text-5xl font-black text-white">
                FlowConsensus
              </h2>
            </div>
            <p className="text-lg text-gray-400 mb-2">
              Hybrid DAG + Sharding Architecture
            </p>
          </motion.div>
          
          {/* Enhanced Hero Statement - EVM Focus */}
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.6 }}
            className="mb-4"
          >
            <h3 className="text-3xl md:text-4xl font-bold text-blue-400">
              Layer 1 EVM Compatible
            </h3>
          </motion.div>
          
          <p className="text-xl md:text-2xl text-gray-200 mb-3 max-w-4xl mx-auto font-bold">
            Hybrid Consensus for Ultra-High Performance DeFi
          </p>
          
          <p className="text-base md:text-lg text-gray-400 max-w-3xl mx-auto mb-6">
            Experimental research project on distributed consensus architectures
          </p>
          
          {/* Quick Access to Documentation */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="flex justify-center gap-4 mt-8"
          >
            <Link
              to="/whitepaper"
              className="flex items-center gap-2 bg-white hover:bg-blue-400 text-black hover:text-white font-bold py-3 px-6 border-2 border-white transition-all"
            >
              <FileText size={20} />
              <span>Technical Whitepaper</span>
            </Link>
            <Link
              to="/whitepaper"
              className="flex items-center gap-2 bg-black hover:bg-green-400 text-white font-bold py-3 px-6 border-2 border-white transition-all"
            >
              <BookOpen size={20} />
              <span>Litepaper (Simple Guide)</span>
            </Link>
          </motion.div>
        </motion.section>

        {/* Scientific Introduction - Technical Deep Dive */}
        <section className="mb-20">
          <div className="grid md:grid-cols-2 gap-6 max-w-7xl mx-auto">
            
            {/* BlockDAG Theory */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-gray-950 border-2 border-white rounded-none p-8"
            >
              <div className="flex items-center gap-3 mb-6">
                <GitBranch className="w-8 h-8 text-white" />
                <h3 className="text-2xl font-bold text-white">BlockDAG Architecture</h3>
              </div>
              
              {/* ASCII Diagram */}
              <div className="bg-black border border-white rounded-none p-4 mb-4 font-mono text-xs text-white overflow-x-auto">
                <pre>{`    B1 ──→ B3 ──→ B5
    ↑  ╲    ↑  ╲    ↑
    │   ╲   │   ╲   │
    B0   ↘  B2   ↘  B4 ──→ B6
         ╲   ↑    ╲   ↑
          ↘  │     ↘  │
            ╲│      ╲│
             B7      B8

GHOSTDAG: k=3 параметр
Throughput: O(n × k) paralelo`}</pre>
              </div>
              
              <p className="text-sm text-gray-300 leading-relaxed mb-4">
                Implementation of Directed Acyclic Graph with GHOSTDAG protocol for topological ordering. 
                Enables parallel block processing without sacrificing consistency.
              </p>
              
              <div className="flex items-start gap-2 text-xs text-gray-400">
                <span className="text-white">◉</span>
                <span>Paper: "PHANTOM GHOSTDAG: A Scalable Generalization of Nakamoto Consensus" (Sompolinsky et al.)</span>
              </div>
            </motion.div>

            {/* Consensus Algorithm */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-gray-950 border-2 border-white rounded-none p-8"
            >
              <div className="flex items-center gap-3 mb-6">
                <Cpu className="w-8 h-8 text-white" />
                <h3 className="text-2xl font-bold text-white">FlowConsensus Protocol</h3>
              </div>
              
              {/* Pseudocode */}
              <div className="bg-black border border-white rounded-none p-4 mb-4 font-mono text-xs text-white overflow-x-auto">
                <pre>{`fn validate_block(block: Block) -> Result<()> {
  // 1. BLS Signature Aggregation
  let agg_sig = aggregate_signatures(
    block.validators
  );
  verify_bls(agg_sig, block.hash)?;
  
  // 2. Threshold Check (>2/3)
  if block.stake_weight < THRESHOLD {
    return Err("Insufficient quorum");
  }
  
  // 3. Cross-Shard Verification
  verify_shard_roots(block.shard_refs)?;
  Ok(())
}`}</pre>
              </div>
              
              <p className="text-sm text-gray-300 leading-relaxed mb-4">
                Hybrid consensus with BLS aggregation for signature efficiency. Cross-shard coordination 
                via merkle roots and atomic verification.
              </p>
              
              <div className="flex items-start gap-2 text-xs text-gray-400">
                <span className="text-white">◉</span>
                <span>Implementation in Rust + libp2p for low-latency P2P networking</span>
              </div>
            </motion.div>

            {/* Sharding Model */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-gray-950 border-2 border-white rounded-none p-8"
            >
              <div className="flex items-center gap-3 mb-6">
                <Network className="w-8 h-8 text-white" />
                <h3 className="text-2xl font-bold text-white">Sharding Architecture</h3>
              </div>
              
              {/* Sharding Diagram */}
              <div className="bg-black border border-white rounded-none p-4 mb-4 font-mono text-xs text-white overflow-x-auto">
                <pre>{`┌─────────────────────────────────┐
│     Beacon Chain / Epochs       │
│   Checkpoint每~1000 bloques     │
└────────┬────────────────┬───────┘
         │                │
    ┌────▼────┐      ┌────▼────┐
    │ Shard 0 │◄────►│ Shard 1 │
    │ State A │      │ State B │
    └────┬────┘      └────┬────┘
         │                │
    ┌────▼────┐      ┌────▼────┐
    │ Shard 2 │◄────►│ Shard 3 │
    │ State C │      │ State D │
    └─────────┘      └─────────┘

Cross-Shard TX via 2PC protocol`}</pre>
              </div>
              
              <p className="text-sm text-gray-300 leading-relaxed mb-4">
                4 shards with state partitioning. Cross-shard transactions via Two-Phase Commit 
                with automatic rollback. Validator rotation every epoch.
              </p>
              
              <div className="flex items-start gap-2 text-xs text-gray-400">
                <span className="text-white">◉</span>
                <span>Based on research from Zilliqa and Ethereum 2.0 sharding proposals</span>
              </div>
            </motion.div>

            {/* Cryptography */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.5 }}
              className="bg-gray-950 border-2 border-white rounded-none p-8"
            >
              <div className="flex items-center gap-3 mb-6">
                <Lock className="w-8 h-8 text-white" />
                <h3 className="text-2xl font-bold text-white">Cryptographic Primitives</h3>
              </div>
              
              {/* Crypto Formula */}
              <div className="bg-black border border-white rounded-none p-4 mb-4 text-white">
                <div className="text-sm mb-3 font-mono">
                  <span className="text-gray-400">BLS Signature:</span>
                  <div className="mt-2 text-xs">
                    σ = H(m)<sup>sk</sup> ∈ G₁
                  </div>
                  <div className="text-xs mt-1">
                    e(σ, g₂) = e(H(m), pk)
                  </div>
                </div>
                
                <div className="border-t border-gray-700 pt-3 mt-3 text-sm font-mono">
                  <span className="text-gray-400">Threshold Encryption:</span>
                  <div className="mt-2 text-xs">
                    C = Enc<sub>pk</sub>(m) → D<sub>1</sub>...D<sub>n</sub>
                  </div>
                  <div className="text-xs mt-1">
                    m = Decrypt(D<sub>1</sub>,...,D<sub>t</sub>) si t ≥ ⌈n/2⌉
                  </div>
                </div>
              </div>
              
              <p className="text-sm text-gray-300 leading-relaxed mb-4">
                BLS signatures (BN254 curve) for efficient aggregation. Threshold encryption (t-of-n) 
                pre-execution for MEV resistance via encrypted mempool.
              </p>
              
              <div className="flex items-start gap-2 text-xs text-gray-400">
                <span className="text-white">◉</span>
                <span>Implementation: blst library (audited) + DLEQ proofs for VRF</span>
              </div>
            </motion.div>

          </div>

          {/* Technical Stats Summary */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="mt-6 max-w-7xl mx-auto bg-black border-2 border-white rounded-none p-6"
          >
            <div className="grid grid-cols-2 md:grid-cols-5 gap-6 text-center">
              <div>
                <p className="text-sm text-gray-400 mb-1">Theoretical TPS</p>
                <p className="text-2xl font-bold text-white">520k+</p>
              </div>
              <div>
                <p className="text-sm text-gray-400 mb-1">Latency Target</p>
                <p className="text-2xl font-bold text-white">&lt;500ms</p>
              </div>
              <div>
                <p className="text-sm text-gray-400 mb-1">Finality</p>
                <p className="text-2xl font-bold text-white">~1 epoch</p>
              </div>
              <div>
                <p className="text-sm text-gray-400 mb-1">Security Model</p>
                <p className="text-2xl font-bold text-white">BFT 2/3</p>
              </div>
              <div>
                <p className="text-sm text-gray-400 mb-1">VM</p>
                <p className="text-2xl font-bold text-white">REVM</p>
              </div>
            </div>
          </motion.div>
        </section>

        {/* Blockchain Composition */}
        <section className="mb-20">
          <h2 className="text-4xl font-bold text-center mb-12 text-white">
            AVO Protocol Architecture
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-6">
            <ArchitectureCard
              icon="◆"
              title="BlockDAG"
              description="BlockDAG + GHOSTDAG for massive throughput without sacrificing decentralization"
            />
            <ArchitectureCard
              icon="◈"
              title="4 Shards"
              description="Parallel processing with native cross-shard atomic transactions"
            />
            <ArchitectureCard
              icon="◉"
              title="BLS Signatures"
              description="Aggregated BLS signatures for efficient validation with >2/3 quorum"
            />
            <ArchitectureCard
              icon="◊"
              title="zk-Proofs"
              description="Zero-knowledge proofs for privacy and efficient state verification"
            />
            <ArchitectureCard
              icon="◐"
              title="Epochs"
              description="Periodic checkpoints every epoch for deterministic finality and validator rotation"
            />
            <ArchitectureCard
              icon="◇"
              title="FlowConsensus"
              description="Cross-shard coordination with threshold encryption for MEV resistance"
            />
          </div>
        </section>

        {/* Comparison Table */}
        <section className="mb-20">
          <h2 className="text-4xl font-bold text-center mb-12 text-white">
            Global TPS Ranking — Simple Transfers
          </h2>
          
          {/* TPS Methodology Disclaimer */}
          <div className="bg-gray-950 border border-white rounded-none p-6 mb-8 max-w-5xl mx-auto">
            <h3 className="text-lg font-bold text-white mb-3">📊 Measurement Methodology</h3>
            <p className="text-sm text-gray-400 mb-3">
              This table shows TPS for <strong className="text-white">simple transfers (pure TX)</strong> - 
              the most basic scenario where a token is sent from A → B without additional logic.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
              <div className="bg-black border border-gray-700 rounded-none p-3">
                <p className="text-gray-400 mb-1">
                  <strong className="text-white">✓ Simple Transfers:</strong>
                </p>
                <p className="text-gray-500">
                  Only native token sending. Higher numbers, but don't represent real-world usage with complex smart contracts.
                </p>
              </div>
              <div className="bg-black border border-gray-700 rounded-none p-3">
                <p className="text-gray-400 mb-1">
                  <strong className="text-white">⚠️ Real Operations:</strong>
                </p>
                <p className="text-gray-500">
                  DeFi swaps, NFT mints, complex contracts. Real TPS is typically 10-100x lower than simple transfers.
                </p>
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full border border-gray-700 rounded-none overflow-hidden">
              <thead className="bg-gray-800">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-bold text-gray-300">Ranking</th>
                  <th className="px-6 py-4 text-left text-sm font-bold text-gray-300">Network / Project</th>
                  <th className="px-6 py-4 text-left text-sm font-bold text-gray-300">TPS (Simple Transfers)</th>
                  <th className="px-6 py-4 text-left text-sm font-bold text-gray-300">Average Finality</th>
                  <th className="px-6 py-4 text-left text-sm font-bold text-gray-300">Consensus / Architecture</th>
                  <th className="px-6 py-4 text-left text-sm font-bold text-gray-300">EVM Compatibility</th>
                  <th className="px-6 py-4 text-left text-sm font-bold text-gray-300">MEV Protection</th>
                </tr>
              </thead>
              <tbody className="bg-gray-900/50">
                <tr className="border-t border-gray-700 bg-green-900/20">
                  <td className="px-6 py-4 text-green-400 font-bold text-lg">★★★★★</td>
                  <td className="px-6 py-4 font-bold text-green-400">AVO Protocol</td>
                  <td className="px-6 py-4 text-green-400 font-semibold">
                    ≈ 520,000 TPS
                    <div className="text-xs text-gray-400 font-normal">(sustained, transfer-only)</div>
                  </td>
                  <td className="px-6 py-4 text-green-400 font-semibold">&lt;1s</td>
                  <td className="px-6 py-4 text-sm">BlockDAG + FlowConsensus</td>
                  <td className="px-6 py-4 text-green-400 font-semibold">✅ REVM Native</td>
                  <td className="px-6 py-4 text-green-400 font-semibold">✅ Threshold Encryption</td>
                </tr>
                <tr className="border-t border-gray-700">
                  <td className="px-6 py-4 font-semibold">★★★★☆</td>
                  <td className="px-6 py-4 font-semibold">Sui</td>
                  <td className="px-6 py-4">
                    ~297,000 TPS
                    <div className="text-xs text-gray-500">(simple transfers, lab)</div>
                  </td>
                  <td className="px-6 py-4">&lt;1s</td>
                  <td className="px-6 py-4 text-sm">Narwhal + Bullshark</td>
                  <td className="px-6 py-4 text-red-400">❌ Move VM</td>
                  <td className="px-6 py-4 text-yellow-400">⚠️ Partial</td>
                </tr>
                <tr className="border-t border-gray-700">
                  <td className="px-6 py-4 font-semibold">★★★☆☆</td>
                  <td className="px-6 py-4 font-semibold">Sei v2</td>
                  <td className="px-6 py-4">~12,500 TPS</td>
                  <td className="px-6 py-4">&lt;1s</td>
                  <td className="px-6 py-4 text-sm">Optimistic Parallelization</td>
                  <td className="px-6 py-4 text-green-400">✅ Yes</td>
                  <td className="px-6 py-4 text-red-400">❌ No</td>
                </tr>
                <tr className="border-t border-gray-700">
                  <td className="px-6 py-4 font-semibold">★★★☆☆</td>
                  <td className="px-6 py-4 font-semibold">Solana</td>
                  <td className="px-6 py-4">
                    3,000–5,000 TPS
                    <div className="text-xs text-gray-500">(real mainnet / up to 65k with vote txs)</div>
                  </td>
                  <td className="px-6 py-4">~13s</td>
                  <td className="px-6 py-4 text-sm">PoH + PoS</td>
                  <td className="px-6 py-4 text-red-400">❌ SVM</td>
                  <td className="px-6 py-4 text-red-400">❌ No</td>
                </tr>
                <tr className="border-t border-gray-700">
                  <td className="px-6 py-4 font-semibold">★★★☆☆</td>
                  <td className="px-6 py-4 font-semibold">zkSync Era</td>
                  <td className="px-6 py-4">~100–200 TPS</td>
                  <td className="px-6 py-4">~10 min</td>
                  <td className="px-6 py-4 text-sm">zk-Rollup (L2)</td>
                  <td className="px-6 py-4 text-green-400">✅ EVM</td>
                  <td className="px-6 py-4 text-green-400">✅ Sequencer</td>
                </tr>
                <tr className="border-t border-gray-700">
                  <td className="px-6 py-4 font-semibold">★★★☆☆</td>
                  <td className="px-6 py-4 font-semibold">StarkNet</td>
                  <td className="px-6 py-4">~100–300 TPS</td>
                  <td className="px-6 py-4">~10 min</td>
                  <td className="px-6 py-4 text-sm">zk-Rollup (L2)</td>
                  <td className="px-6 py-4 text-yellow-400">⚠️ Cairo VM</td>
                  <td className="px-6 py-4 text-green-400">✅ Sequencer</td>
                </tr>
                <tr className="border-t border-gray-700">
                  <td className="px-6 py-4 font-semibold">★★☆☆☆</td>
                  <td className="px-6 py-4 font-semibold">Cardano</td>
                  <td className="px-6 py-4">~250 TPS</td>
                  <td className="px-6 py-4">5–10 min</td>
                  <td className="px-6 py-4 text-sm">Ouroboros PoS</td>
                  <td className="px-6 py-4 text-red-400">❌ No</td>
                  <td className="px-6 py-4 text-red-400">❌ No</td>
                </tr>
                <tr className="border-t border-gray-700">
                  <td className="px-6 py-4 font-semibold">★★☆☆☆</td>
                  <td className="px-6 py-4 font-semibold">Ethereum</td>
                  <td className="px-6 py-4">~15–30 TPS</td>
                  <td className="px-6 py-4">~15 min</td>
                  <td className="px-6 py-4 text-sm">PoS (Gasper)</td>
                  <td className="px-6 py-4 text-green-400">✅ Standard</td>
                  <td className="px-6 py-4 text-yellow-400">⚠️ PBS</td>
                </tr>
                <tr className="border-t border-gray-700">
                  <td className="px-6 py-4 font-semibold">★☆☆☆☆</td>
                  <td className="px-6 py-4 font-semibold">Bitcoin</td>
                  <td className="px-6 py-4">~3–7 TPS</td>
                  <td className="px-6 py-4">~10 min</td>
                  <td className="px-6 py-4 text-sm">PoW (SHA-256)</td>
                  <td className="px-6 py-4 text-red-400">❌ No</td>
                  <td className="px-6 py-4 text-green-400">✅ Native (no MEV)</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="text-center text-gray-400 mt-6 text-sm">
            ⚡ All metrics based on official documentation and public benchmarks. AVO TPS is theoretical capacity with 1,000+ distributed validator network (4 parallel shards).
          </p>
        </section>

        {/* Key Features - Best of Each Blockchain */}
        <section className="mb-20">
          <h2 className="text-4xl font-bold text-center mb-6 text-white">
            Best of Each Blockchain
          </h2>
          <p className="text-center text-gray-400 mb-12 max-w-4xl mx-auto">
            AVO Protocol is an experimental research project combining proven technologies from the most advanced blockchains 
            in a hybrid Layer 1 architecture, surpassing Sei, Sui and Tectum while maintaining full Ethereum compatibility.
          </p>

          {/* Technology Origin Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
            
            {/* Kaspa - BlockDAG */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-gray-950 border-2 border-cyan-500 rounded-none p-6 hover:border-cyan-300 transition-all"
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="text-4xl mb-2 text-cyan-400">◆</div>
                  <h3 className="text-xl font-bold text-white mb-1">BlockDAG + GHOSTDAG</h3>
                  <p className="text-xs text-cyan-400 uppercase tracking-wider font-bold">Inspired by Kaspa</p>
                </div>
              </div>
              <p className="text-sm text-gray-300 leading-relaxed mb-3">
                Directed acyclic graph with block selection via GHOSTDAG. Parallel processing of multiple 
                simultaneous blocks instead of linear chain.
              </p>
              <div className="text-xs text-gray-500 border-t border-cyan-900 pt-3">
                <span className="text-cyan-400 font-semibold">Kaspa</span>: 1 block/sec → <span className="text-white">AVO</span>: 100+ blocks/sec with 4 shards
              </div>
            </motion.div>

            {/* NEAR/Ethereum 2.0 - Sharding */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-gray-950 border-2 border-green-500 rounded-none p-6 hover:border-green-300 transition-all"
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="text-4xl mb-2 text-green-400">◈</div>
                  <h3 className="text-xl font-bold text-white mb-1">Dynamic Sharding</h3>
                  <p className="text-xs text-green-400 uppercase tracking-wider font-bold">Inspired by NEAR & ETH 2.0</p>
                </div>
              </div>
              <p className="text-sm text-gray-300 leading-relaxed mb-3">
                4 specialized shards process transactions in parallel with cross-shard atomic transactions. 
                Horizontal scalability via state partitioning.
              </p>
              <div className="text-xs text-gray-500 border-t border-green-900 pt-3">
                <span className="text-green-400 font-semibold">NEAR</span> nightshade + <span className="text-purple-400 font-semibold">Ethereum</span> danksharding research
              </div>
            </motion.div>

            {/* Ethereum - BLS Signatures */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-gray-950 border-2 border-purple-500 rounded-none p-6 hover:border-purple-300 transition-all"
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="text-4xl mb-2 text-purple-400">◉</div>
                  <h3 className="text-xl font-bold text-white mb-1">BLS Aggregation</h3>
                  <p className="text-xs text-purple-400 uppercase tracking-wider font-bold">Inspired by Ethereum PoS</p>
                </div>
              </div>
              <p className="text-sm text-gray-300 leading-relaxed mb-3">
                Aggregated BLS signatures reduce cryptographic overhead from O(n) to O(1). Validator quorum 
                {'>'} 2/3 for commits with efficient batch verification.
              </p>
              <div className="text-xs text-gray-500 border-t border-purple-900 pt-3">
                <span className="text-purple-400 font-semibold">Ethereum</span> Beacon Chain + blst library (audited)
              </div>
            </motion.div>

            {/* zkSync/StarkNet - zk-Proofs */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-gray-950 border-2 border-blue-500 rounded-none p-6 hover:border-blue-300 transition-all"
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="text-4xl mb-2 text-blue-400">◊</div>
                  <h3 className="text-xl font-bold text-white mb-1">zk-Proofs (Zero-Knowledge)</h3>
                  <p className="text-xs text-blue-400 uppercase tracking-wider font-bold">Inspired by zkSync & StarkNet</p>
                </div>
              </div>
              <p className="text-sm text-gray-300 leading-relaxed mb-3">
                Zero-knowledge proofs for transaction privacy and succinct state verification. 
                Efficient validation without revealing sensitive data.
              </p>
              <div className="text-xs text-gray-500 border-t border-blue-900 pt-3">
                <span className="text-blue-400 font-semibold">zkSync</span> circuits + optional privacy layer
              </div>
            </motion.div>

            {/* Cardano/Ethereum - Epochs */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="bg-gray-950 border-2 border-red-500 rounded-none p-6 hover:border-red-300 transition-all"
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="text-4xl mb-2 text-red-400">◐</div>
                  <h3 className="text-xl font-bold text-white mb-1">Epochs & Checkpoints</h3>
                  <p className="text-xs text-red-400 uppercase tracking-wider font-bold">Inspired by Cardano & Ethereum</p>
                </div>
              </div>
              <p className="text-sm text-gray-300 leading-relaxed mb-3">
                Epoch system for deterministic finality. Each epoch marks a checkpoint with validator 
                rotation and state consolidation. Guaranteed irreversible progress.
              </p>
              <div className="text-xs text-gray-500 border-t border-red-900 pt-3">
                <span className="text-red-400 font-semibold">Cardano</span> Ouroboros epochs + <span className="text-purple-400 font-semibold">Ethereum</span> finality gadget
              </div>
            </motion.div>

            {/* Avalanche - Consensus */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="bg-gray-950 border-2 border-orange-500 rounded-none p-6 hover:border-orange-300 transition-all"
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="text-4xl mb-2 text-orange-400">◇</div>
                  <h3 className="text-xl font-bold text-white mb-1">Hybrid FlowConsensus</h3>
                  <p className="text-xs text-orange-400 uppercase tracking-wider font-bold">Inspired by Avalanche DAG</p>
                </div>
              </div>
              <p className="text-sm text-gray-300 leading-relaxed mb-3">
                Coordinated consensus protocol across shards with threshold encryption for MEV resistance. 
                Guaranteed atomicity in multi-shard operations.
              </p>
              <div className="text-xs text-gray-500 border-t border-orange-900 pt-3">
                <span className="text-orange-400 font-semibold">Avalanche</span> Snowman + cross-shard coordination layer
              </div>
            </motion.div>

            {/* Chainlink VRF - Randomness */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
              className="bg-gray-950 border-2 border-yellow-500 rounded-none p-6 hover:border-yellow-300 transition-all"
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="text-4xl mb-2 text-yellow-400">◑</div>
                  <h3 className="text-xl font-bold text-white mb-1">VRF + Threshold Encryption</h3>
                  <p className="text-xs text-yellow-400 uppercase tracking-wider font-bold">Inspired by Chainlink & Flashbots</p>
                </div>
              </div>
              <p className="text-sm text-gray-300 leading-relaxed mb-3">
                Verifiable Random Functions for validator selection and pre-execution threshold encryption. 
                Prevents maximal extractable value (MEV) via reordering.
              </p>
              <div className="text-xs text-gray-500 border-t border-yellow-900 pt-3">
                <span className="text-yellow-400 font-semibold">Chainlink</span> VRF v2 + <span className="text-gray-400 font-semibold">Flashbots</span> SUAVE research
              </div>
            </motion.div>

          </div>

          {/* Performance Comparison */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
            className="mt-12 bg-black border-2 border-white rounded-none p-8 max-w-7xl mx-auto"
          >
            <h3 className="text-2xl font-bold text-white mb-6 text-center">
              Theoretical Capacity with Distributed Network
            </h3>

            <div className="bg-gray-950 border border-white rounded-none p-6 mb-6 text-center">
              <p className="text-sm text-gray-400 mb-4">
                With a distributed network of over <strong className="text-white">1,000+ validator nodes</strong>, 
                AVO Protocol's multi-shard architecture is capable of processing:
              </p>
              <p className="text-6xl font-bold text-white mb-2">520,000+</p>
              <p className="text-xl text-gray-400">Transactions Per Second</p>
              <div className="mt-4 pt-4 border-t border-gray-700 text-xs text-gray-500">
                Parallel BlockDAG + 4 shards + BLS aggregation + optimized batch processing
              </div>
            </div>

            <div className="border-t border-white pt-6">
              <p className="text-sm text-gray-400 mb-4 text-center">
                Comparison with other high-speed L1s:
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center text-xs">
                <div>
                  <p className="text-gray-500 mb-1">Sei v2</p>
                  <p className="text-white font-mono">12.5k TPS</p>
                </div>
                <div>
                  <p className="text-gray-500 mb-1">Sui</p>
                  <p className="text-white font-mono">~297k TPS</p>
                </div>
                <div>
                  <p className="text-gray-500 mb-1">Solana</p>
                  <p className="text-white font-mono">~65k TPS</p>
                </div>
                <div>
                  <p className="text-gray-500 mb-1">Ethereum</p>
                  <p className="text-white font-mono">~15 TPS</p>
                </div>
              </div>
            </div>

            <div className="mt-6 pt-6 border-t border-white">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div className="flex items-start gap-2">
                  <span className="text-white">◉</span>
                  <span className="text-gray-300">
                    <strong className="text-white">EVM Compatible:</strong> Unlike Sui (Move), AVO uses REVM for full Ethereum compatibility
                  </span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-white">◉</span>
                  <span className="text-gray-300">
                    <strong className="text-white">True Decentralization:</strong> Unlike Tectum, AVO has publicly verifiable BFT consensus
                  </span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-white">◉</span>
                  <span className="text-gray-300">
                    <strong className="text-white">MEV Protection:</strong> Native threshold encryption, not available in Sei or Sui
                  </span>
                </div>
              </div>
            </div>
          </motion.div>
        </section>
      </div>
    </div>
  );
};

const FeatureDetailCard = ({ icon, title, description }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5 }}
    className="bg-gray-950 border border-white rounded-none p-6 shadow-lg hover:shadow-xl transition-shadow duration-300"
  >
    <div className="text-5xl mb-4 text-white">{icon}</div>
    <h3 className="text-2xl font-bold mb-3 text-white">{title}</h3>
    <p className="text-gray-300 leading-relaxed">{description}</p>
  </motion.div>
);

const ArchitectureCard = ({ icon, title, description }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5 }}
    className="bg-gray-900 border border-white rounded-none p-6 hover:border-gray-400 transition-all"
  >
    <div className="text-4xl mb-3 text-white">{icon}</div>
    <h3 className="text-xl font-bold mb-2 text-white">{title}</h3>
    <p className="text-gray-400 text-sm leading-relaxed">{description}</p>
  </motion.div>
);

export default Home;